import CircularProgress from "@material-ui/core/CircularProgress"
import Typography from "@material-ui/core/Typography"
import { Redirect } from "react-router-dom"
import { useGetUserMembershipsQuery } from "../memberships/api"
import { useUrlWithContext } from "../hooks/use-url-with-context"
import { paths } from "../paths"
import { MembershipViewModel } from "../memberships/api/membership"

type DefaultAuthenticatedRedirectProps = {
  user: User
}

type User = {
  id: string
  isActive: boolean
  isWaitingVerification: boolean
  isDeactivated: boolean
}

type UserMembershipsQuery = {
  activeMemberships: MembershipViewModel[]
  isLoading: boolean
  isFetching: boolean
  isError: boolean
}

const useUserMemberships = (user: User) => {
  const query = useGetUserMembershipsQuery(user.id)
  const memberships = query.data || []
  const activeMemberships = memberships.filter((membership) => !membership.isDeactivated)

  return {
    ...query,
    activeMemberships,
  }
}

export const DefaultAuthenticatedRedirect = ({ user }: DefaultAuthenticatedRedirectProps) => {
  const userMembershipsQuery = useUserMemberships(user)
  const isLoading = useIsDefaultAuthenticatedRedirectLoading(user, userMembershipsQuery)
  const redirectPath = useDefaultAuthenticatedRedirectPath(user, userMembershipsQuery)

  if (isLoading) return <RedirectingIndicator />
  return <Redirect to={redirectPath} />
}

const useIsDefaultAuthenticatedRedirectLoading = (user: User, userMembershipsQuery: UserMembershipsQuery): boolean => {
  if (user.isDeactivated || user.isWaitingVerification) return false
  return userMembershipsQuery.isLoading || userMembershipsQuery.isFetching
}

const useDefaultAuthenticatedRedirectPath = (user: User, userMembershipsQuery: UserMembershipsQuery): string => {
  const { createPathWithGivenContext } = useUrlWithContext()
  const defaultPath = createPathWithGivenContext({ path: paths.home(), mainContextId: user.id })

  if (user.isDeactivated) return createPathWithGivenContext({ path: paths.deactivated(), mainContextId: user.id })
  if (user.isWaitingVerification)
    return createPathWithGivenContext({ path: paths.accountVerification(), mainContextId: user.id })
  if (userMembershipsQuery.isError) return defaultPath

  const { activeMemberships } = userMembershipsQuery
  const [membership] = activeMemberships
  const hasSingleActiveMembership = activeMemberships.length === 1
  return hasSingleActiveMembership
    ? createPathWithGivenContext({
        path: paths.home(),
        mainContextId: membership.orgId,
        subContextId: membership.id,
      })
    : defaultPath
}

const RedirectingIndicator = () => {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <CircularProgress size={20} />
      <Typography variant="body2">Redirecting...</Typography>
    </div>
  )
}
